import React, { useMemo, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListItemText,
    Checkbox,
    IconButton,
    InputAdornment,
    Tooltip,
    Typography,
    Stack,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import TableChartIcon from '@mui/icons-material/TableChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import ClearIcon from '@mui/icons-material/Clear';
import { SelectChangeEvent } from '@mui/material/Select';
import { CutterHistory, Report } from '../../types/cutterHistory';
import CountersLineChart from './CutterDetailsHistory.countersChart';
import PresetSelector from '../../common/PresetSelector';


// ── Types ────────────────────────────────────────────────────────────────────

/** One parsed counter snapshot from a Report row. */
export interface CounterRow {
    id: string;             // "<serialNumber>|<rowNumber>"
    rowNumber: number;
    zipCreationDate: Date;
    zipFileName: string;
    zipStoredOnDisk: boolean;
    [counterKey: string]: string | number | Date | boolean;
}

// ── Shared filter state ──────────────────────────────────────────────────────

export interface CountersFilterState {
    viewMode: 'table' | 'chart';
    selectedCounters: string[];
    dateRangeValue: number[];   // [0,0] = no brush active; set by D3 brush
}

export const initialCountersFilterState: CountersFilterState = {
    viewMode: 'chart',
    selectedCounters: [],
    dateRangeValue: [0, 0],
};


// ── Data parsing ─────────────────────────────────────────────────────────────

function parseCounters(reports: Report[]): { rows: CounterRow[]; counterKeys: string[] } {
    const keySet = new Set<string>();
    const rows: CounterRow[] = [];

    for (const report of reports) {
        if (!report.counters) continue;
        const parsed: Record<string, number> = report.counters;
        const row: CounterRow = {
            id: `${report.serialNumber}|${report.rowNumber}`,
            rowNumber: report.rowNumber,
            zipCreationDate: new Date(report.zipCreationDate),
            zipFileName: report.zipFileName,
            zipStoredOnDisk: report.zipStoredOnDisk,
        };
        for (const [k, v] of Object.entries(parsed)) {
            row[k] = v;
            keySet.add(k);
        }
        rows.push(row);
    }

    // Sort ascending by date so the chart reads left → right
    rows.sort((a, b) => a.zipCreationDate.getTime() - b.zipCreationDate.getTime());

    return { rows, counterKeys: Array.from(keySet).sort((a, b) => a.localeCompare(b)) };
}


// ── Component props ──────────────────────────────────────────────────────────

export interface CutterDetailsHistoryCountersProps {
    cutter: CutterHistory;
    isDetached?: boolean;
    filterState: CountersFilterState;
    onFilterChange: (patch: Partial<CountersFilterState>) => void;
    onExportDataChange?: (data: any[], columns: Array<{ key: string; header: string }>) => void;
}


// ── Component ────────────────────────────────────────────────────────────────

export const CutterDetailsHistoryCounters: React.FC<CutterDetailsHistoryCountersProps> = ({
    cutter,
    isDetached,
    filterState,
    onFilterChange,
    onExportDataChange,
}) => {
    const { viewMode, selectedCounters, dateRangeValue } = filterState;

    // ── Parse once (memoised) ────────────────────────────────────────────────
    const { rows: allRows, counterKeys } = useMemo(
        () => parseCounters(cutter.reports),
        [cutter.reports]
    );

    // Initialise selectedCounters to the first 5 keys on first load
    const hasInitialised = React.useRef(false);
    useEffect(() => {
        if (!hasInitialised.current && counterKeys.length > 0) {
            hasInitialised.current = true;
            onFilterChange({ selectedCounters: counterKeys.slice(0, 5) });
        }
    }, [counterKeys, onFilterChange]);

    // ── Filtering by date range ──────────────────────────────────────────────
    const filteredRows = useMemo(() => {
        if (dateRangeValue[0] === 0) return allRows;
        return allRows.filter(r => {
            const t = r.zipCreationDate.getTime();
            return t >= dateRangeValue[0] && t <= dateRangeValue[1];
        });
    }, [allRows, dateRangeValue]);

    // ── DataGrid columns ─────────────────────────────────────────────────────
    const columns = useMemo<GridColDef[]>(() => {
        const fixed: GridColDef[] = [
            {
                field: 'zipCreationDate',
                headerName: 'Date',
                width: 160,
                valueFormatter: (value: Date) =>
                    value ? new Date(value).toLocaleString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                    }) : '',
                renderCell: (params) => (
                    <Tooltip title={params.row.zipFileName as string} placement="right" arrow>
                        <span>{params.formattedValue as string}</span>
                    </Tooltip>
                ),
            },
        ];
        const counterCols: GridColDef[] = (selectedCounters.length > 0 ? selectedCounters : []).map(k => ({
            field: k,
            headerName: k,
            width: 140,
            type: 'number' as const,
            valueFormatter: (value: number) =>
                value == null ? '' : Number.isInteger(value) ? value.toString() : value.toFixed(3),
        }));
        return [...fixed, ...counterCols];
    }, [selectedCounters, counterKeys]);

    // ── Export data ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!onExportDataChange) return;
        const exportCols = columns.map(c => ({ key: c.field, header: c.headerName ?? c.field }));
        onExportDataChange(filteredRows, exportCols);
    }, [filteredRows, columns, onExportDataChange]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleViewModeChange = useCallback(
        (_e: React.MouseEvent<HTMLElement>, v: 'table' | 'chart' | null) => {
            if (v !== null) onFilterChange({ viewMode: v });
        },
        [onFilterChange]
    );

    const handleCounterChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
            // MUI appends undefined when the no-value "Select all/none" MenuItem is clicked; that case is handled by onMouseDown
            if ((value as unknown[]).includes(undefined)) return;
            onFilterChange({ selectedCounters: value });
        },
        [onFilterChange]
    );

    const isFiltered = selectedCounters.length < counterKeys.length || dateRangeValue[0] !== 0;

    return (
        <Stack direction="column" sx={{ p: 1, width: '100%', height: '100%', minHeight: '60vh', minWidth: 0, overflow: 'hidden' }}>
            {/* ── Controls ── */}
            <Stack direction="row" gap={1} mb={0.5} flexWrap="nowrap" alignItems="center">
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    sx={{ '& .MuiToggleButton-root': { px: 0.75, py: 0.5, minWidth: 36 } }}
                >
                    <ToggleButton value="table">
                        <TableChartIcon sx={{ fontSize: 18 }} />
                    </ToggleButton>
                    <ToggleButton value="chart">
                        <TimelineIcon sx={{ fontSize: 18 }} />
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* Counter multi-select */}
                <FormControl size="small" sx={{ minWidth: 180, maxWidth: 320 }}>
                    <InputLabel>Counters</InputLabel>
                    <Select<string[]>
                        multiple
                        value={selectedCounters}
                        label="Counters"
                        onChange={handleCounterChange}
                        endAdornment={
                            selectedCounters.length < counterKeys.length ? (
                                <InputAdornment position="end" sx={{ position: 'absolute', right: 28 }}>
                                    <IconButton size="small" onClick={() => onFilterChange({ selectedCounters: counterKeys })}>
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined
                        }
                        renderValue={(selected: string[]) =>
                            selected.length === 0 ? 'None'
                            : selected.length === counterKeys.length ? 'All'
                            : `${selected.length} selected`
                        }
                        sx={{ fontSize: '0.875rem' }}
                    >
                        {/* Select all / Select none toggle */}
                        <MenuItem
                            dense
                            onMouseDown={(e) => {
                                e.preventDefault(); // keep dropdown open
                                onFilterChange({ selectedCounters: selectedCounters.length === counterKeys.length ? [] : counterKeys });
                            }}
                        >
                            <Checkbox
                                checked={selectedCounters.length === counterKeys.length}
                                indeterminate={selectedCounters.length > 0 && selectedCounters.length < counterKeys.length}
                                size="small"
                            />
                            <ListItemText
                                primary={selectedCounters.length === counterKeys.length ? 'Select none' : 'Select all'}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            />
                        </MenuItem>
                        <Divider />

                        {counterKeys.map(k => (
                            <MenuItem key={k} value={k} dense>
                                <Checkbox checked={selectedCounters.includes(k)} size="small" />
                                <ListItemText primary={k} primaryTypographyProps={{ variant: 'body2' }} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Presets */}
                <PresetSelector
                    selectedItems={selectedCounters}
                    onSelectionChange={(items) => onFilterChange({ selectedCounters: items })}
                    storageKey="counters-selector"
                />

                {/* Reset all */}
                {isFiltered && (
                    <Tooltip title="Reset all filters">
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                            onClick={() => onFilterChange({ selectedCounters: counterKeys, dateRangeValue: [0, 0] })}
                            sx={{ flexShrink: 0, py: 0.25, lineHeight: 1, whiteSpace: 'nowrap' }}
                        >
                            Reset filters
                        </Button>
                    </Tooltip>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
                    {filteredRows.length} report{filteredRows.length !== 1 ? 's' : ''}
                </Typography>
            </Stack>

            {/* ── Content ── */}
            <Box sx={{ flex: 1, minHeight: 'calc(60vh - 48px)', overflow: 'auto' }}>
                {allRows.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            No counters data available for this cutter
                        </Typography>
                    </Box>
                ) : viewMode === 'table' ? (
                    <DataGrid
                        autoHeight
                        rows={filteredRows}
                        columns={columns}
                        density="compact"
                        pagination
                        initialState={{ pagination: { paginationModel: { pageSize: isDetached ? 25 : 10 } } }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        disableRowSelectionOnClick
                        sx={{ display: 'flex', flexDirection: 'column-reverse' }}
                    />
                ) : (
                    filteredRows.length > 0 ? (
                        <CountersLineChart
                            rows={filteredRows}
                            selectedCounters={selectedCounters}
                            xDomainOverride={dateRangeValue[0] !== 0 ? [new Date(dateRangeValue[0]), new Date(dateRangeValue[1])] : null}
                            onBrushChange={(range) => onFilterChange({
                                dateRangeValue: range ? [range[0].getTime(), range[1].getTime()] : [0, 0],
                            })}
                        />
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">
                                No data available for the selected filters
                            </Typography>
                        </Box>
                    )
                )}
            </Box>
        </Stack>
    );
};

export default CutterDetailsHistoryCounters;
